import { TExtendedMeetingRoom } from "apis/meeting-room-device";
import React, { useEffect } from "react";
import {
  BatteryInfoLabel,
  Container,
  DeviceIdLabel,
  DevicePositionLabel,
  Header,
  TableContainer,
} from "./DeviceListItem.styles";
import MeetingRoomItem from "./MeetingRoomItem";
import UI from "@mile-meeting/mile-ui";
import ToolTip from "uis/new-tootip";
import DeviceInfoItem from "./DeviceInfoItem";
import styled from "@emotion/styled";

type DeviceListItemProps = {
  meetingRoom: TExtendedMeetingRoom;
  foldState: "FOLD" | "UNFOLD" | undefined;
  initFoldSate: () => void;
};

export type LinkState = "LINKED" | "UNLINKED_INSIDE" | "UNLINKED_OUTSIDE" | "UNLINKED_BOTH_TABLETS";

const DeviceListItem: React.FC<DeviceListItemProps> = ({ meetingRoom, foldState = undefined, initFoldSate }) => {
  const [showTable, setShowTable] = React.useState<boolean>(true);

  const getLinkState = (): LinkState => {
    //* 내/외부 태블릿 모두 연동된 경우
    if (meetingRoom.isInsideTabletUsed && meetingRoom.isOutsideTabletUsed) {
      if (
        meetingRoom.meetingRoomDevices.some((device) => device.location === "INSIDE") &&
        meetingRoom.meetingRoomDevices.some((device) => device.location === "OUTSIDE")
      ) {
        return "LINKED";
      } else if (meetingRoom.meetingRoomDevices.some((device) => device.location === "INSIDE")) {
        return "UNLINKED_OUTSIDE";
      } else if (meetingRoom.meetingRoomDevices.some((device) => device.location === "OUTSIDE")) {
        return "UNLINKED_INSIDE";
      } else return "UNLINKED_BOTH_TABLETS";
    }

    //* 내부 태블릿만 연동된 경우
    if (meetingRoom.isInsideTabletUsed && !meetingRoom.isOutsideTabletUsed) {
      if (meetingRoom.meetingRoomDevices.some((device) => device.location === "INSIDE")) {
        return "LINKED";
      } else {
        return "UNLINKED_INSIDE";
      }
    }

    //* 외부 태블릿만 연동된 경우
    if (!meetingRoom.isInsideTabletUsed && meetingRoom.isOutsideTabletUsed) {
      if (meetingRoom.meetingRoomDevices.some((device) => device.location === "OUTSIDE")) {
        return "LINKED";
      } else {
        return "UNLINKED_OUTSIDE";
      }
    }

    return "UNLINKED_BOTH_TABLETS";
  };

  useEffect(() => {
    if (foldState === undefined) {
      return;
    }
    if (foldState === "FOLD") {
      setShowTable(false);
      return;
    }
    if (foldState === "UNFOLD") {
      setShowTable(true);
      return;
    }
  }, [foldState]);

  return (
    <Container>
      <MeetingRoomItem
        meetingRoom={meetingRoom}
        linkState={getLinkState()}
        onHandleArrow={() => {
          setShowTable(!showTable);
          if (foldState !== undefined) initFoldSate();
        }}
        showTable={showTable}
      />
      <TableContainer isShow={showTable}>
        <Header>
          <DevicePositionLabel>
            <UI.Typography type="label01" color="var(--alias-color-label-normal)">
              기기 위치
            </UI.Typography>
          </DevicePositionLabel>
          <DeviceIdLabel>
            <UI.Typography type="label01" color="var(--alias-color-label-normal)">
              기기 식별 변호
            </UI.Typography>
            <ToolTip
              text={
                <p>
                  태블릿 Mile Meeting 앱의 관리자 메뉴(워크스페이스 아이콘을 탭하면 등장하는 팝업) 하단에
                  <br />
                  기재되어 있는 번호입니다.
                </p>
              }
              position={{
                top: -52,
                left: -40,
              }}
              icon={<UI.Icon name="info" size={16} color="var(--alias-color-label-assistive)" />}
            />
          </DeviceIdLabel>
          <BatteryInfoLabel>
            <UI.Typography type="label01" color="var(--alias-color-label-normal)">
              배터리/충전
            </UI.Typography>
          </BatteryInfoLabel>
        </Header>
        {meetingRoom.meetingRoomDevices.length === 0 && (
          <EmptyRow>
            <UI.Icon name="removeCircle" size={24} color="var(--alias-color-label-alternative)" />
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
              연동된 기기가 없습니다. 회의실 내외부에 비치된 태블릿 상태를 확인해 주세요.
            </UI.Typography>
          </EmptyRow>
        )}
        {meetingRoom.meetingRoomDevices.map((device) => (
          <DeviceInfoItem key={device.id} {...device} />
        ))}
      </TableContainer>
    </Container>
  );
};

export default React.memo(DeviceListItem);

const EmptyRow = styled.div`
  display: flex;
  padding: 12px 24px;
  align-items: center;
  align-self: stretch;
  border-bottom: 1px solid var(--alias-color-line-normal);
  gap: 8px;
`;
